import React, { useState, useMemo } from 'react'
import { Tabs } from '../../FbUI/Tabs'
import { UnmatchedDoorsTable } from '../../DataExpansionComponents/UnmatchedDoors/UnmatchedDoorsTable'
import { TabbedDoorSectionProps } from './types'
import { IContactCompany } from 'models/contact_companies'
import { FilterIdentifier } from 'models/saved_view'
import { formatInteger } from '../../../utils/formatting'
import { StandardSummaryResponse } from 'models/paginated-response'

interface DoorsTableTabsProps {
  forChainProxyId?: number
  chainID?: number
  chainData?: TabbedDoorSectionProps['chainData']
  subCategory: TabbedDoorSectionProps['subCategory']
  matchedTableComponent: TabbedDoorSectionProps['matchedTableComponent']
  matchedTableProps?: TabbedDoorSectionProps['matchedTableProps']
  matchedTableKey?: string
  unmatchedTableKeyPrefix: string
  className?: string
}

export function DoorsTableTabs({
  forChainProxyId,
  chainID,
  chainData,
  subCategory,
  matchedTableComponent: MatchedTableComponent,
  matchedTableProps = {},
  matchedTableKey,
  unmatchedTableKeyPrefix,
  className = '',
}: DoorsTableTabsProps) {
  const [selectedTab, setSelectedTab] = useState<'matched' | 'unmatched'>(
    'matched'
  )
  const [unmatchedCount, setUnmatchedCount] = useState(0)

  // Create chainProxy object for UnmatchedDoorsTable
  const chainProxy: IContactCompany | undefined = useMemo(() => {
    if (!chainData || !subCategory) return undefined

    return {
      id: forChainProxyId || chainData.contact_company_id || 0,
      domain: chainData.domain || '',
      name: chainData.name || subCategory.name,
      sub_category: subCategory,
    } as IContactCompany
  }, [chainData, subCategory, forChainProxyId])

  const handleUnmatchedCountChange = (summary: StandardSummaryResponse) => {
    setUnmatchedCount(summary.alt_count || 0)
  }

  const totalCount = chainData?.count || 0
  const unmatchedTableKey = `${unmatchedTableKeyPrefix}-${chainData?.id}`

  return (
    <div className={`w-full ${className}`}>
      <Tabs
        tabs={[
          {
            key: 'matched',
            title: `First Bite (${formatInteger(totalCount)})`,
          },
          {
            key: 'unmatched',
            title: `Unmatched (${formatInteger(unmatchedCount)})`,
          },
        ]}
        selectedTab={selectedTab}
        setSelectedTab={setSelectedTab}
      />

      <div>
        <div
          className={`max-h-[600px] flex pb-1 pt-4 ${
            selectedTab === 'matched' ? '' : 'hidden'
          }`}
        >
          <MatchedTableComponent
            chainID={chainID}
            forChainProxyId={forChainProxyId}
            chainData={chainData}
            chainProxy={chainProxy}
            unmatchedTableKey={unmatchedTableKey}
            tableKey={
              matchedTableKey || `${subCategory.slug}-table-${chainData?.id}`
            }
            {...matchedTableProps}
          />
        </div>

        <div
          className={`max-h-[600px] flex pb-1 pt-4 ${
            selectedTab === 'unmatched' ? '' : 'hidden'
          }`}
        >
          {chainProxy && (
            <UnmatchedDoorsTable
              tableKey={unmatchedTableKey}
              filterIdentifierModifier={
                chainProxy.id.toString() as FilterIdentifier
              }
              chainProxy={chainProxy}
              setTotalRowsCount={handleUnmatchedCountChange}
            />
          )}
        </div>
      </div>
    </div>
  )
}
